<template>
  <section class="relative flex flex-col">
    <div class="flex flex-col items-start gap-2.5 p-4 pt-0 flex-1 self-stretch">
      <figure
        :style="{
          backgroundImage: `url(${data.prize?.image || '/contests/default-prize.png'})`,
        }"
        class="relative flex justify-center items-center gap-2.5 flex-1 self-stretch rounded-xl bg-lightgray bg-cover bg-center bg-no-repeat shadow-[0px_1px_4px_1px_rgba(0,0,0,0.19)]"
      >
        <PointScoreBadge
          v-if="!channelStore.isLeaderboardDisabled"
          coins="100"
          class="size-8 !absolute right-4 top-4"
        />

        <div
          class="flex h-6 p-2.5 justify-center items-center gap-1 absolute left-2 top-2 rounded-29u bg-fill-dark backdrop-blur-xl"
        >
          <Icon icon="mdi:users" class="text-xs" />
          <p class="text-xs text-text">
            {{ data.user_participation }}
            {{
              $t(
                `channel.contests.raffle.participants.${data.user_participation === 1 ? "one" : "other"}`,
              )
            }}
          </p>
        </div>
      </figure>
    </div>

    <div class="flex p-4 justify-between items-start self-stretch">
      <div
        class="flex flex-col justify-center items-center gap-1 self-stretch w-full"
      >
        <p
          class="self-stretch text-center text-text font-serif text-lg font-normal leading-normal w-full"
        >
          {{ raffleTitle }}
        </p>
      </div>
    </div>

    <div class="flex flex-col justify-between gap-4">
      <div
        v-if="data.scheduled_end_time"
        class="flex flex-row justify-center items-center gap-2"
      >
        <Counter :data="countdownData" :priority="priority" />
      </div>

      <div v-if="canJoinRaffle" class="p-1.5 px-4 pb-5">
        <!-- Button -->
        <ContestButton
          class="w-full"
          :priority="priority"
          :disabled="loading || disabled"
          :is-loading="loading"
          @click="onRaffleJoin"
        >
          <div class="flex items-center gap-1">
            <Icon
              v-if="isPaidRaffle"
              icon="mdi:lock"
              class="size-6 text-fill-gold"
            />
            <span class="text-text font-serif text-base font-normal">
              {{ $t("channel.contests.raffle.actions.enterNow") }}
            </span>
          </div>
        </ContestButton>
      </div>
    </div>

    <template v-if="false">
      <span
        v-if="!userIsCreator && (expired || data.has_joined)"
        class="text-text mt-4 mb-4 font-serif text-center text-base font-normal leading-5"
      >
        {{ $t("channel.contests.raffle.status.waitingWinner") }}
      </span>
    </template>
  </section>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const RaffleJoinFragment = gql(/* GraphQL */ `
  fragment RaffleJoinData on Raffle {
    id
    title
    description
    has_joined
    user_participation
    scheduled_end_time
    created_by {
      id
      username
    }
    prize {
      title
      image
    }
    raffle_translations {
      title
      description
    }
    raffle_communities {
      community {
        id
        name
      }
    }
    authorization_rule_group {
      ...AuthorizationRuleGroupForGating
    }
  }
`);

export type RaffleJoinData = ResultOf<typeof RaffleJoinFragment>;
</script>

<script setup lang="ts">
import { JoinRaffleDocument } from "@/generated/graphql";
import { Icon } from "@iconify/vue";
import PointScoreBadge from "./point-score-badge.vue";
import Counter from "./countdown-display.vue";
import { useMutation } from "@urql/vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { type CardPriority } from "./creation/contests";
import { ChannelPermission } from "@f3/shared/permissions";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import ContestButton from "./contest-button.vue";
import { computed } from "vue";
import { useObjectTranslation } from "@/stores/translation.store";
import { useIsGated } from "@/features/gating/evaluation/use-is-gated";
import { useChannelStore } from "@/stores/channel.store";

const trackingService = useTrackingStore();
const channelStore = useChannelStore();
const emit = defineEmits<{
  join: [];
  delete: [];
}>();

const props = defineProps<{
  data: RaffleJoinData;
  variant?: "responsive" | "regular";
  loading: boolean;
  priority: CardPriority;
  countdownData: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  expired: boolean;
  disabled?: boolean;
  canJoinRaffle: boolean;
  userIsCreator: boolean;
}>();

const { text: raffleTitle } = useObjectTranslation(
  computed(() => {
    const raffle = props.data;
    const originalText = props.data.title ?? props.data.description;
    if (!raffle || !originalText) return null;

    return {
      authorUserId: raffle.created_by?.id,
      originalText: originalText,
      objectAccessors: {
        objectId: raffle.id,
        objectProperty: ["title", "description"],
      },
      translatedText:
        raffle.raffle_translations[0]?.title ??
        raffle.raffle_translations[0]?.description,
    };
  }),
);

const { executeMutation: joinRaffle } = useMutation(JoinRaffleDocument);

const { handler: joinRaffleAction, loading } = useAwaitAction(async () => {
  await joinRaffle({
    raffle_id: props.data.id,
    show_notification: true,
  });
  trackingService.track({
    event: TrackEventEnum.ChannelRaffleEntered,
    category: EventCategory.ChannelRaffle,
    metadata: {
      prize: props.data?.prize?.title ?? "",
      experienceId: props.data.id,
      experienceType: ExperienceType.Raffle,
      experienceAction: ExperienceAction.RAFFLE_JOIN,
      experienceTitle: props.data?.title ?? "",
      experienceCreatorId: props.data?.created_by?.id,
      experienceCreatorUsername: props.data?.created_by?.username ?? undefined,
      // experienceMembership: channelStore.channelMemberships.find(
      //   (cm) => cm.authorization_rule_id === props.data?.authorization_rule_id,
      // )?.name,
      experienceCommunityIds: props.data?.raffle_communities?.map(
        (rc) => rc.community.id,
      ),
      experienceCommunityNames: props.data?.raffle_communities
        ?.map((rc) => rc.community.name)
        .filter((name): name is string => name != null),
    },
  });
  emit("join");
});

const { handler: onRaffleJoin, silentHandler: onRaffleJoinSilent } =
  useGatingForAction(ChannelPermission.interactInContests, joinRaffleAction, {
    gatingExperience: {
      experienceId: props.data?.id,
      experienceType: ExperienceType.Raffle,
      experienceAction: ExperienceAction.RAFFLE_JOIN,
      experienceTitle: props.data?.title ?? undefined,
      experienceCreatorId: props.data?.created_by?.id,
      experienceCreatorUsername: props.data?.created_by?.username ?? undefined,
      experienceCommunityIds: props.data?.raffle_communities?.map(
        (c) => c.community.id,
      ),
      experienceCommunityNames: props.data?.raffle_communities
        ?.map((c) => c.community.name)
        .filter((name): name is string => name != null),
      // experienceMembership: channelStore.channelMemberships.find(
      //   (cm) => cm.authorization_rule_id === props.data?.authorization_rule_id,
      // )?.name,
    },
  });

const isPaidRaffle = useIsGated(
  () => props.data.authorization_rule_group,
  ChannelPermission.interactInContests,
);

defineExpose({
  joinRaffle: onRaffleJoinSilent,
});
</script>
